import { useState } from 'react'
import { useAuth } from '@clerk/clerk-react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Link, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'

type Step1 = {
  shop_name: string
  city: string
  province: string
  labor_rate: string
  tax_rate: string
  tax_label: string
  phone: string
}

type Step2 = {
  owner_email: string
  owner_name: string
  assistant_name: string
  plan: 'alpha' | 'beta'
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

/**
 * Curated onboarding wizard — 3 steps:
 *   1) Shop details
 *   2) Owner invite
 *   3) Review & send
 *
 * On submit: POST /api/admin/tenants. The Worker creates the Clerk org,
 * sends the owner invitation, and inserts a pending `shops` row. The
 * Clerk webhook (organizationMembership.created) flips
 * provisioned_at to now() on the owner's first accept.
 */
export function AdminNewTenant() {
  const { t } = useTranslation('admin')
  const { t: tCommon } = useTranslation('common')
  const { getToken, isLoaded } = useAuth()
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const [step, setStep] = useState<1 | 2 | 3>(1)
  const [s1, setS1] = useState<Step1>({
    shop_name: '',
    city: '',
    province: 'ON',
    labor_rate: '100',
    tax_rate: '13',
    tax_label: 'HST',
    phone: '',
  })
  const [s2, setS2] = useState<Step2>({
    owner_email: '',
    owner_name: '',
    assistant_name: '',
    plan: 'beta',
  })
  const [errors, setErrors] = useState<Record<string, string>>({})

  const submitMut = useMutation({
    mutationFn: async () => {
      const token = await getToken()
      if (!token) throw new Error(tCommon('error.missing_token'))
      const body = {
        shop_name: s1.shop_name.trim(),
        city: s1.city.trim() || null,
        province: s1.province.trim() || null,
        phone: s1.phone.trim() || null,
        labor_rate_cad: parseFloat(s1.labor_rate) || 100,
        tax_rate: parseFloat(s1.tax_rate) / 100 || 0.13,
        tax_label: s1.tax_label.trim() || 'HST',
        owner_email: s2.owner_email.trim(),
        owner_name: s2.owner_name.trim() || null,
        assistant_name: s2.assistant_name.trim(),
        plan: s2.plan,
      }
      const res = await fetch('/api/admin/tenants', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
      })
      if (!res.ok) {
        const text = await res.text().catch(() => '')
        throw new Error(text || `request_failed:${res.status}`)
      }
      return (await res.json()) as {
        tenant: { id: string; name: string; clerk_org_id: string }
      }
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'tenants'] })
      navigate(`/tenants/${data.tenant.id}`)
    },
  })

  function validateStep1(): boolean {
    const e: Record<string, string> = {}
    if (!s1.shop_name.trim()) e.shop_name = t('new_tenant.errors.shop_name_required')
    setErrors(e)
    return Object.keys(e).length === 0
  }

  function validateStep2(): boolean {
    const e: Record<string, string> = {}
    if (!s2.owner_email.trim()) {
      e.owner_email = t('new_tenant.errors.owner_email_required')
    } else if (!EMAIL_RE.test(s2.owner_email.trim())) {
      e.owner_email = t('new_tenant.errors.owner_email_invalid')
    }
    if (!s2.assistant_name.trim()) {
      e.assistant_name = t('new_tenant.errors.assistant_name_required')
    }
    setErrors(e)
    return Object.keys(e).length === 0
  }

  if (!isLoaded) {
    return (
      <Card className="p-4 text-sm text-muted-foreground">
        {tCommon('loading')}
      </Card>
    )
  }

  return (
    <div className="space-y-5">
      <Link
        to="/"
        className="text-xs text-muted-foreground hover:text-foreground"
      >
        {t('new_tenant.back')}
      </Link>

      <div>
        <h1 className="text-2xl font-bold tracking-tight text-foreground">
          {t('new_tenant.title')}
        </h1>
        <p className="mt-1 max-w-2xl text-sm text-muted-foreground">
          {t('new_tenant.subtitle')}
        </p>
      </div>

      {/* Stepper */}
      <div className="flex flex-wrap items-center gap-2 text-xs font-semibold">
        <StepPill
          n={1}
          label={t('new_tenant.stepper.shop_details')}
          state={step >= 1 ? 'active' : 'todo'}
          done={step > 1}
        />
        <div className="hidden h-px w-6 bg-border sm:block" />
        <StepPill
          n={2}
          label={t('new_tenant.stepper.owner_invite')}
          state={step >= 2 ? 'active' : 'todo'}
          done={step > 2}
        />
        <div className="hidden h-px w-6 bg-border sm:block" />
        <StepPill
          n={3}
          label={t('new_tenant.stepper.review')}
          state={step >= 3 ? 'active' : 'todo'}
          done={false}
        />
      </div>

      {step === 1 ? (
        <Card className="max-w-xl space-y-4 p-5">
          <div className="text-xs font-bold uppercase tracking-wide text-brand">
            {t('new_tenant.step_label', { n: 1 })}
          </div>
          <h2 className="text-base font-semibold">
            {t('new_tenant.step_1.title')}
          </h2>

          <Field
            label={t('new_tenant.step_1.shop_name')}
            error={errors.shop_name}
          >
            <Input
              value={s1.shop_name}
              onChange={(e) => setS1({ ...s1, shop_name: e.target.value })}
              placeholder={t('new_tenant.step_1.shop_name_placeholder')}
            />
          </Field>

          <Field label={t('new_tenant.step_1.city')}>
            <div className="grid grid-cols-3 gap-2">
              <div className="col-span-2">
                <Input
                  value={s1.city}
                  onChange={(e) => setS1({ ...s1, city: e.target.value })}
                  placeholder={t('new_tenant.step_1.city_placeholder')}
                />
              </div>
              <Input
                value={s1.province}
                onChange={(e) => setS1({ ...s1, province: e.target.value })}
                placeholder="ON"
              />
            </div>
          </Field>

          <div className="grid grid-cols-2 gap-3">
            <Field label={t('new_tenant.step_1.labor_rate')}>
              <Input
                value={s1.labor_rate}
                onChange={(e) =>
                  setS1({ ...s1, labor_rate: e.target.value })
                }
                inputMode="decimal"
                placeholder={t('new_tenant.step_1.labor_rate_placeholder')}
              />
            </Field>
            <Field label={t('new_tenant.step_1.tax')}>
              <div className="grid grid-cols-3 gap-2">
                <Input
                  value={s1.tax_rate}
                  onChange={(e) =>
                    setS1({ ...s1, tax_rate: e.target.value })
                  }
                  inputMode="decimal"
                  placeholder="13"
                />
                <div className="col-span-2">
                  <Input
                    value={s1.tax_label}
                    onChange={(e) =>
                      setS1({ ...s1, tax_label: e.target.value })
                    }
                    placeholder="HST"
                  />
                </div>
              </div>
            </Field>
          </div>

          <Field label={t('new_tenant.step_1.phone')}>
            <Input
              value={s1.phone}
              onChange={(e) => setS1({ ...s1, phone: e.target.value })}
              placeholder={t('new_tenant.step_1.phone_placeholder')}
            />
          </Field>

          <Button
            onClick={() => {
              if (validateStep1()) setStep(2)
            }}
            className="w-full"
          >
            {t('new_tenant.step_1.continue')}
          </Button>
        </Card>
      ) : null}

      {step === 2 ? (
        <Card className="max-w-xl space-y-4 p-5">
          <div className="text-xs font-bold uppercase tracking-wide text-brand">
            {t('new_tenant.step_label', { n: 2 })}
          </div>
          <h2 className="text-base font-semibold">
            {t('new_tenant.step_2.title')}
          </h2>

          <Field
            label={t('new_tenant.step_2.owner_email')}
            error={errors.owner_email}
          >
            <Input
              value={s2.owner_email}
              onChange={(e) =>
                setS2({ ...s2, owner_email: e.target.value })
              }
              type="email"
              placeholder={t('new_tenant.step_2.owner_email_placeholder')}
            />
          </Field>

          <Field label={t('new_tenant.step_2.owner_name')}>
            <Input
              value={s2.owner_name}
              onChange={(e) =>
                setS2({ ...s2, owner_name: e.target.value })
              }
              placeholder={t('new_tenant.step_2.owner_name_placeholder')}
            />
          </Field>

          <Field
            label={t('new_tenant.step_2.assistant_name')}
            error={errors.assistant_name}
            help={t('new_tenant.step_2.assistant_name_caption')}
          >
            <Input
              value={s2.assistant_name}
              onChange={(e) =>
                setS2({ ...s2, assistant_name: e.target.value })
              }
              placeholder={t('new_tenant.step_2.assistant_name_placeholder')}
            />
          </Field>

          <Field label={t('new_tenant.step_2.plan')}>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setS2({ ...s2, plan: 'alpha' })}
                className={
                  s2.plan === 'alpha'
                    ? 'flex-1 rounded-lg border-2 border-brand bg-brand-light/60 px-3 py-2 text-sm font-semibold text-brand-dark'
                    : 'flex-1 rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground hover:bg-muted/40'
                }
              >
                {t('new_tenant.step_2.plan_alpha')}
              </button>
              <button
                type="button"
                onClick={() => setS2({ ...s2, plan: 'beta' })}
                className={
                  s2.plan === 'beta'
                    ? 'flex-1 rounded-lg border-2 border-brand bg-brand-light/60 px-3 py-2 text-sm font-semibold text-brand-dark'
                    : 'flex-1 rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground hover:bg-muted/40'
                }
              >
                {t('new_tenant.step_2.plan_beta')}
              </button>
            </div>
          </Field>

          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => setStep(1)}
              className="flex-1"
            >
              {t('new_tenant.step_2.back')}
            </Button>
            <Button
              onClick={() => {
                if (validateStep2()) setStep(3)
              }}
              className="flex-1"
            >
              {t('new_tenant.step_2.continue')}
            </Button>
          </div>
        </Card>
      ) : null}

      {step === 3 ? (
        <Card className="max-w-xl space-y-4 p-5">
          <div className="text-xs font-bold uppercase tracking-wide text-brand">
            {t('new_tenant.step_label', { n: 3 })}
          </div>
          <h2 className="text-base font-semibold">
            {t('new_tenant.step_3.title')}
          </h2>

          {/* Email preview */}
          <div className="rounded-lg border border-border bg-brand-tint/60 p-4">
            <div className="mb-3 flex items-center gap-2">
              <div className="grid size-7 place-items-center rounded-lg bg-brand text-[10px] font-bold text-white">
                A
              </div>
              <div>
                <div className="text-[11px] font-semibold leading-tight">
                  autoflo
                </div>
                <div className="text-[10px] text-muted-foreground">
                  noreply@autoflo.app
                </div>
              </div>
            </div>
            <div className="text-sm font-semibold text-foreground">
              {t('new_tenant.step_3.email_preview.subject', {
                shop: s1.shop_name,
              })}
            </div>
            <p className="mt-1 text-xs text-muted-foreground">
              {t('new_tenant.step_3.email_preview.body', {
                name: s2.owner_name ? ' ' + s2.owner_name.split(' ')[0] : '',
              })}
            </p>
            <Button size="sm" className="mt-3">
              {t('new_tenant.step_3.email_preview.cta')}
            </Button>
          </div>

          <div className="rounded-lg border border-border bg-background p-3 text-xs">
            <div className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
              {t('new_tenant.step_3.summary_title')}
            </div>
            <SummaryRow
              label={t('new_tenant.step_3.summary.creates_org')}
              value={s1.shop_name}
            />
            <SummaryRow
              label={t('new_tenant.step_3.summary.owner_role')}
              value={t('new_tenant.step_3.summary.owner_role_value')}
            />
            <SummaryRow
              label={t('new_tenant.step_3.summary.provision_shop')}
              value={t('new_tenant.step_3.summary.provision_shop_when')}
              valueClass="text-emerald-700"
            />
            <SummaryRow
              label={t('new_tenant.step_3.summary.audit_entry')}
              value={t('new_tenant.step_3.summary.ok')}
              valueClass="text-emerald-700 font-semibold"
            />
          </div>

          {submitMut.isError ? (
            <Card className="border-destructive/30 bg-destructive/10 p-3 text-xs text-destructive">
              {t('new_tenant.errors.submit')}
              {submitMut.error instanceof Error
                ? ` (${submitMut.error.message})`
                : null}
            </Card>
          ) : null}

          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => setStep(2)}
              disabled={submitMut.isPending}
              className="flex-1"
            >
              {t('new_tenant.step_3.back')}
            </Button>
            <Button
              onClick={() => submitMut.mutate()}
              disabled={submitMut.isPending}
              className="flex-1"
            >
              {submitMut.isPending
                ? t('new_tenant.step_3.sending')
                : t('new_tenant.step_3.send')}
            </Button>
          </div>
        </Card>
      ) : null}
    </div>
  )
}

function Field({
  label,
  children,
  error,
  help,
}: {
  label: string
  children: React.ReactNode
  error?: string
  help?: string
}) {
  return (
    <div className="space-y-1">
      <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        {label}
      </Label>
      {children}
      {help ? (
        <div className="text-[11px] text-muted-foreground">{help}</div>
      ) : null}
      {error ? <div className="text-xs text-destructive">{error}</div> : null}
    </div>
  )
}

function StepPill({
  n,
  label,
  state,
  done,
}: {
  n: number
  label: string
  state: 'active' | 'todo'
  done: boolean
}) {
  const containerClass = done
    ? 'bg-brand-light text-brand-dark'
    : state === 'active'
      ? 'bg-brand text-white'
      : 'bg-muted text-muted-foreground'
  const numClass = done
    ? 'bg-brand text-white'
    : state === 'active'
      ? 'bg-white text-brand'
      : 'bg-muted-foreground/40 text-white'
  return (
    <span
      className={`inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-xs ${containerClass}`}
    >
      <span
        className={`grid size-5 place-items-center rounded-full text-[10px] font-bold ${numClass}`}
      >
        {n}
      </span>
      {label}
    </span>
  )
}

function SummaryRow({
  label,
  value,
  valueClass,
}: {
  label: string
  value: string
  valueClass?: string
}) {
  return (
    <div className="flex justify-between gap-3 py-1">
      <span className="text-muted-foreground">{label}</span>
      <span className={valueClass ?? 'text-foreground'}>{value}</span>
    </div>
  )
}
